// @archigraph r8DljQiU
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/Button';
import { Container } from './ui/Container';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[60vh] flex items-center justify-center">
          <Container>
            <div className="max-w-md">
              <p className="text-xs uppercase tracking-widest text-error mb-4 font-bold">
                // fatal error
              </p>
              <h2 className="text-2xl font-bold text-text mb-2">
                Something went wrong
              </h2>
              <p className="text-xs text-text-lighter mb-6">
                An unexpected error occurred.
              </p>
              {this.state.error && (
                <pre className="text-xs text-error bg-error-light border border-error/30 p-4 mb-6 overflow-auto font-mono">
                  {this.state.error.message}
                </pre>
              )}
              <div className="flex gap-3">
                <Button variant="ghost" onClick={this.handleReset}>
                  Try again
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => window.location.assign('/')}
                >
                  Go home
                </Button>
              </div>
            </div>
          </Container>
        </div>
      );
    }

    return this.props.children;
  }
}
